"use client";

/**
 * The ⓘ next to a section's title: what the section is, what you get out of it, and what to do with it.
 * Card and Panel show it by themselves when lib/sectionHelp.ts has an entry for their title, or when a page
 * passes `info` directly. Opens on click (and keyboard), closes on outside click, Escape or scroll.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";
import type { SectionHelp } from "@/lib/sectionHelp";

export function SectionInfo({ help, label }: { help: SectionHelp; label: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const r = btn.current?.getBoundingClientRect();
    if (r) {
      const w = Math.min(360, window.innerWidth - 24);
      setPos({ left: Math.min(Math.max(12, r.left + r.width / 2 - w / 2), window.innerWidth - w - 12), top: r.bottom + 8 });
    }
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const scroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", key);
    window.addEventListener("scroll", scroll, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", key);
      window.removeEventListener("scroll", scroll, true);
    };
  }, [open]);
  return (
    <>
      <button ref={btn} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={`About “${label}”`} aria-expanded={open} title="What is this?"
        className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full align-middle text-rb-faint transition hover:bg-rb-brand-soft hover:text-rb-brand-deep">
        <Info size={14} />
      </button>
      {open && pos && typeof document !== "undefined" && createPortal(
        <div ref={box} role="dialog" aria-label={label} className="fixed z-[1200] w-[min(360px,calc(100vw-24px))] rounded-2xl border border-rb-line bg-white p-4 text-left text-rb-ink shadow-2xl"
          style={{ left: pos.left, top: Math.min(pos.top, window.innerHeight - 340) }}>
          <div className="flex items-start justify-between gap-2">
            <p className="text-[14px] font-bold">{label}</p>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full p-0.5 text-rb-faint hover:bg-rb-surface-2" aria-label="Close"><X size={15} /></button>
          </div>
          <dl className="mt-2 flex flex-col gap-2 text-[12.5px] leading-snug">
            <div><dt className="font-semibold">What it is</dt><dd className="text-rb-muted">{help.what}</dd></div>
            <div><dt className="font-semibold">How it helps you</dt><dd className="text-rb-muted">{help.why}</dd></div>
            {help.how && <div><dt className="font-semibold">What to do</dt><dd className="text-rb-muted">{help.how}</dd></div>}
          </dl>
        </div>,
        document.body,
      )}
    </>
  );
}
